/**
 * Границы учёта: с какого месяца есть данные и с какого зарплата — доля.
 *
 * Лаборатория работала и раньше, но в программе её жизнь начинается с января
 * 2026-го: до него учёт вёлся в тетрадях и таблицах, и переносить его никто
 * не станет. Месяцы левее этой черты не пустые — их просто НЕТ, и показывать
 * по ним нули значит уверять, что в декабре 2025-го не было ни заказов, ни
 * зарплат.
 *
 * Отдельная черта — сентябрь 2026-го: с него техникам платят долю от дохода.
 * До него платили сдельно, по таблицам учёта работ, и суммы вносились в
 * программу руками. Проценту там взяться неоткуда, и показывать «доля 9 % от
 * дохода» за август — выдумка: ставка появилась позже самого месяца.
 */
import { monthKeyForOffset } from './dates.js';
/** Первый месяц, за который в программе есть данные. */
export const FIRST_MONTH_KEY = '2026-01';
/** Первый месяц, за который зарплата считается долей от дохода. */
export const FIRST_SHARE_MONTH_KEY = '2026-09';
/**
 * Считается ли зарплата этого месяца долей от дохода.
 *
 * Сравнение строк, а не дат: ключ `YYYY-MM` упорядочен лексикографически —
 * `'2026-08' < '2026-09'` и `'2025-12' < '2026-01'` без разбора чисел.
 */
export function shareApplies(monthKey) {
    return monthKey >= FIRST_SHARE_MONTH_KEY;
}
/** Есть ли в программе данные за этот месяц. */
export function monthInRecords(monthKey) {
    return monthKey >= FIRST_MONTH_KEY;
}
/** Самый ранний месяц, который можно открыть: дальше листать некуда. */
export function isFirstMonth(monthKey) {
    return monthKey <= FIRST_MONTH_KEY;
}
/**
 * Ключ месяца, сдвинутый на `offset`: `monthKeyShift('2026-09', -1)` → `'2026-08'`.
 *
 * Переключателям и графикам месяц известен ключом, а не датой; первое число
 * подставляется здесь, чтобы это не повторялось в каждом разделе.
 */
export function monthKeyShift(monthKey, offset) {
    return monthKeyForOffset(`${monthKey}-01`, offset);
}
/**
 * Ряд месяцев, кончая `lastMonthKey`, от старых к новым.
 *
 * Обрезается началом учёта: ряд «последние полгода» от февраля 2026-го — это
 * два столбика, а не шесть, четыре из которых в 2025 году. Пустой ряд тоже
 * возможен — если весь запрошенный кусок лежит до января 2026-го.
 */
export function monthKeysUpTo(lastMonthKey, count) {
    const ключи = [];
    for (let i = count - 1; i >= 0; i -= 1) {
        const ключ = monthKeyShift(lastMonthKey, -i);
        if (monthInRecords(ключ))
            ключи.push(ключ);
    }
    return ключи;
}
/**
 * Месяцы ТЕКУЩЕГО года — с января по нынешний месяц.
 *
 * Для диаграмм, которые не должны переезжать вслед за выбранным месяцем.
 * Ряд «последние полгода» считался от показанного месяца: нажал на крайний
 * левый столбик — и весь ряд сдвинулся, столбики поменяли высоту, а у начала
 * учёта их оставалось два, потом один, и диаграмма пропадала совсем. Год —
 * величина неподвижная: столбики стоят на месте, нажатие только переключает
 * месяц.
 *
 * Будущие месяцы не показываются: столбик с нулём за декабрь в сентябре
 * читается как «не заработал», хотя он попросту не наступил.
 */
export function monthKeysOfYear(today) {
    const год = today.slice(0, 4);
    const последний = today.slice(0, 7);
    const ключи = [];
    for (let м = 1; м <= 12; м += 1) {
        const ключ = `${год}-${String(м).padStart(2, '0')}`;
        if (ключ > последний)
            break;
        if (monthInRecords(ключ))
            ключи.push(ключ);
    }
    return ключи;
}
//# sourceMappingURL=periods.js.map